import { useFormikContext } from 'formik'
import React, { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { useModal } from '@context/ModalsContext'

import { Body2 } from '@ui/Typography'
import { Link } from '@ui/components/Link'

import { isPaymentProviderPaymentAgentType } from '@model/PaymentProviderType'
import type { WalletPaymentAgentTransferInformationDto } from '@model/WalletPaymentAgentTransferInformationDto'

import { createFormCheckBoxField } from '@global/formField/FormCheckBoxField'

import { Box } from '@shared/components/Box'
import { PaymentTermsConsent } from '@shared/components/Funding/FundingTermsConditions/PaymentTermsConsent'
import { TermsModal } from '@shared/components/Funding/FundingTermsConditions/modals/FundingDepositTerms.modal'

import { useWithdrawalTracker } from '@analytics/amplitude/withdrawal'

import type { WithdrawalFormValues } from '../WithdrawalFlowLayout/types'

const FormCheckBoxField = createFormCheckBoxField()

export const WithdrawalTermsConditions: React.FC<{
  paymentAgent?: WalletPaymentAgentTransferInformationDto
  onPowerOfAttorneyAccepted?: () => void
  showPaymentTermsConsent?: boolean
}> = ({ paymentAgent, onPowerOfAttorneyAccepted, showPaymentTermsConsent = true }) => {
  const { t } = useTranslation()
  const { values, setFieldValue } = useFormikContext<WithdrawalFormValues>()

  const categoryId = values.paymentProvider?.providerCategory?.id
  const isPaymentAgent = isPaymentProviderPaymentAgentType(categoryId)

  return (
    <Box flex direction='column' gap='x12' fullWidth>
      {isPaymentAgent ? (
        <FormCheckBoxField
          name='terms.powerOfAttorney'
          value={values.terms.powerOfAttorney}
          onChange={(v) => {
            setFieldValue('terms.powerOfAttorney', v)
            if (v) onPowerOfAttorneyAccepted?.()
          }}
          size='md'
        >
          <Body2>
            {t('I have read, understood and consent to the')}{' '}
            <Link size='md' href={paymentAgent?.powerOfAttorneyUrl}>
              {t('Payment Agent.Power of Attorney')}
            </Link>
            {'.'}
          </Body2>
        </FormCheckBoxField>
      ) : null}

      {showPaymentTermsConsent && <WithdrawalPaymentTermsConsent />}
    </Box>
  )
}

export const WithdrawalPaymentTermsConsent: React.FC = () => {
  const { t } = useTranslation()
  const { values, setFieldValue } = useFormikContext<WithdrawalFormValues>()
  const tracker = useWithdrawalTracker()

  const termsAndConditions = useMemo(() => {
    const paymentProvider = values.paymentProvider
    return (
      paymentProvider?.parameters?.termsConditions?.map(({ name }) => {
        const currency = paymentProvider.currency?.id ?? ''
        const minAmount = paymentProvider.currency?.minAmount?.toString() ?? '0'
        const maxAmount = paymentProvider.currency?.maxAmount?.toString() ?? '0'
        return name
          .replace(/{minAmount}/g, minAmount)
          .replace(/{maxAmount}/g, maxAmount)
          .replace(/{paymentProviderCurrency}/g, currency)
      }) ?? []
    )
  }, [values.paymentProvider])

  const termsModal = useModal(TermsModal, {
    title: t('Wallet.General Terms and Conditions for Withdrawals'),
    terms: termsAndConditions,
  })

  useEffect(() => {
    if (!values.terms.paymentOperations) {
      setFieldValue('terms.paymentOperations', true)
    }
  }, [setFieldValue, values.terms.paymentOperations])

  const handleOpen = () => {
    tracker.paymentTermsConditionsOpened()
    termsModal.open()
  }

  return <PaymentTermsConsent onOpen={handleOpen} />
}
